import { Box, Typography, Grid } from '@mui/material'
import { AutoFixHigh } from '@mui/icons-material'
import { designSpecs } from '../../theme/designSpecs'

// Import example images
import tshirtBefore from '../../assets/dashboard/tshirt-before.png'
import tshirtAfter from '../../assets/dashboard/tshirt-after.png'
import perfumeBefore from '../../assets/dashboard/perfume-before.png'
import perfumeAfter from '../../assets/dashboard/perfume-after.png'
import handbagBefore from '../../assets/dashboard/handbag-before.png'
import handbagAfter from '../../assets/dashboard/handbag-after.png'
import braceletBefore from '../../assets/dashboard/bracelet-before.png'
import braceletAfter from '../../assets/dashboard/bracelet-after.png'
import bottleBefore from '../../assets/dashboard/bottle-before.png'
import bottleAfter from '../../assets/dashboard/bottle-after.png'

const examples = [
  {
    title: 'T-Shirt',
    before: tshirtBefore,
    after: tshirtAfter,
    prompt:
      'Place this flat-lay t-shirt on a modern lifestyle background. Keep the t-shirt unchanged. Use soft natural lighting, subtle shadows, and a minimal aesthetic. The final image should look like a premium fashion brand website photo.',
  },
  {
    title: 'Perfume',
    before: perfumeBefore,
    after: perfumeAfter,
    prompt:
      'Place this perfume on a glass surface with soft reflections, champagne-colored background, luxury minimalist style',
  },
  {
    title: 'Handbag',
    before: handbagBefore,
    after: handbagAfter,
    prompt:
      'Create a premium fashion editorial image of this handbag. Keep the bag unchanged. Use an elegant background, soft directional lighting, and realistic shadows. The final image should feel luxurious and high-end.',
  },
  {
    title: 'Bracelet',
    before: braceletBefore,
    after: braceletAfter,
    prompt:
      'Create a luxury close-up product photo of this bracelet. Keep the bracelet unchanged. Use soft, controlled lighting that highlights fine details and textures, with a clean, elegant background.',
  },
  {
    title: 'Water Bottle',
    before: bottleBefore,
    after: bottleAfter,
    prompt:
      'Create a fitness lifestyle product photo of this water bottle. Keep the bottle unchanged. Place it in a clean, active setting with bright natural lighting and a modern aesthetic.',
  },
]

const ExampleCard = ({ title, before, after, prompt }) => {
  return (
    <Box
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.6)',
        borderRadius: designSpecs.borderRadius.xl,
        p: 2.5,
        border: '1px solid',
        borderColor: 'grey.100',
      }}
    >
      {/* Before/After Images */}
      <Grid container spacing={0} sx={{ borderRadius: designSpecs.borderRadius.lg, overflow: 'hidden' }}>
        {/* Before Image */}
        <Grid item xs={6}>
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={before}
              alt={`${title} - Before`}
              sx={{
                width: '100%',
                height: 450,
                objectFit: 'contain',
                display: 'block',
                bgcolor: 'grey.50',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(8px)',
                px: 1.5,
                py: 0.5,
                borderRadius: designSpecs.borderRadius.md,
              }}
            >
              <Typography
                sx={{
                  fontSize: designSpecs.typography.sizes.xs,
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                Before
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* After Image */}
        <Grid item xs={6}>
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={after}
              alt={`${title} - After`}
              sx={{
                width: '100%',
                height: 450,
                objectFit: 'contain',
                display: 'block',
                bgcolor: 'grey.50',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor: 'primary.main',
                px: 1.5,
                py: 0.5,
                borderRadius: designSpecs.borderRadius.md,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <AutoFixHigh sx={{ fontSize: designSpecs.typography.sizes.xs, color: 'white' }} />
              <Typography
                sx={{
                  fontSize: designSpecs.typography.sizes.xs,
                  fontWeight: 600,
                  color: 'white',
                }}
              >
                After
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Prompt */}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <AutoFixHigh sx={{ color: 'primary.main', fontSize: designSpecs.iconSizes.sm, mt: 0.25 }} />
        <Typography
          sx={{
            fontSize: designSpecs.typography.sizes.sm,
            color: 'grey.700',
            lineHeight: 1.6,
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: 'grey.800' }}>
            Prompt:{' '}
          </Box>
          <Box component="span" sx={{ fontStyle: 'italic' }}>
            "{prompt}"
          </Box>
        </Typography>
      </Box>
    </Box>
  )
}

const ExamplePrompts = () => {
  return (
    <Box>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1.5, fontWeight: 600, color: 'grey.900' }}>
          Get Inspired
        </Typography>
        <Typography variant="body1" sx={{ color: 'grey.600' }}>
          See how simple prompts turn basic product photos into professional visuals
        </Typography>
      </Box>

      {/* Example Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {examples.map((example, index) => (
          <ExampleCard key={index} {...example} />
        ))}
      </Box>
    </Box>
  )
}

export default ExamplePrompts
